import isUrl from 'is-url'
import { isValidPhoneNumber } from 'libphonenumber-js'

export const requiredFn = (value: string) => (value ? undefined : 'This field is required.')

// Pattern has nested quantifiers; pair with EMAIL_MAX_LENGTH guard in emailFn
// to bound backtracking. Don't use this regex on unbounded input. The TLD is
// {2,} (not {2,6}) so long TLDs like .technology/.healthcare validate — it's a
// simple trailing match, so total backtracking is still bounded by the length guard.
/* eslint-disable security/detect-unsafe-regex */
export const PATTERN_EMAIL = new RegExp(
  /^([0-9a-zA-Z]+[-._+])*[0-9a-zA-Z]+@[0-9a-zA-Z]+([-.][0-9a-zA-Z]+)*([0-9a-zA-Z]*[.])(?!con$)[a-zA-Z]{2,}$/
)
/* eslint-enable security/detect-unsafe-regex */

// RFC 5321 caps email addresses at 254 characters; reject longer input before
// running PATTERN_EMAIL to bound regex backtracking.
const EMAIL_MAX_LENGTH = 254

const PATTERN_NUMBER = new RegExp(/^[0-9]*$/)

// eslint-disable-next-line security/detect-unsafe-regex -- false positive: anchored, fixed-length alternation
const PATTERN_ZIP_CODE = /^(\d{5}(-\d{4})?|[A-Z]\d[A-Z] ?\d[A-Z]\d)$/

export const numberFn = (number: string) =>
  number && !PATTERN_NUMBER.test(number) ? 'Must be valid number.' : undefined

export const phoneNumberFn = (number: string) => {
  return number && !isValidPhoneNumber(number, 'US') ? 'Must be valid number.' : undefined
}

export const validateFilledInput = (
  value: string | undefined | null,
  validatorFn: (value: string) => undefined | string
) => {
  if (!value) {
    return undefined
  }

  return validatorFn(value)
}

export const emailFn = (email: string) => {
  if (!email) {
    return undefined
  }
  if (email.length > EMAIL_MAX_LENGTH) {
    return 'Must be valid email.'
  }
  return PATTERN_EMAIL.test(email) ? undefined : 'Must be valid email.'
}

export const zipCodeFn = (zipCode: string) =>
  zipCode && !PATTERN_ZIP_CODE.test(zipCode) ? 'Must be valid zip code.' : undefined

export const urlFn = (url: string) => (url && !isUrl(url) ? 'Must be a valid URL.' : undefined)

export const composeValidators =
  (validators: Array<(value: string) => undefined | string>) => (value: string) =>
    validators.reduce<undefined | string>(
      (error, validator) => error || validator(value),
      undefined
    )

export const processFloatNumberInput = (input: string) => {
  // replace "," with "." to allow decimal point
  let value = input.replaceAll(',', '.')
  // Only allow numbers and decimal point
  value = value.replaceAll(/[^\d.]/g, '')

  if (value === '.') {
    return '0.'
  }

  // Prevent multiple decimal points
  const parts = value.split('.')
  return parts[0] + (parts.length > 1 ? '.' + parts[1] : '')
}
